import { useQuery } from '@tanstack/react-query'
import { formsApi } from '@api/forms'
import api from '@api/axios'
import { useAuthStore } from '@store/authStore'

/**
 * Form Stats Hook
 * Works for admin, dashboard, form_manager, and department users
 */
export const useFormStats = () => {
    const { user } = useAuthStore()
    const formId = user?.form_id
    const isFormManager = user?.user_type === 'form_manager'
    const isAdmin = user?.user_type === 'admin'
    const isDashboardUser = user?.user_type === 'dashboard'
    const isDepartmentUser = user?.user_type === 'department'

    // For department users, fetch only their navigated submissions
    const {
        data: deptSubmissionsData,
        isLoading: deptSubmissionsLoading,
        refetch: refetchDeptSubmissions,
    } = useQuery({
        queryKey: ['department-submissions-stats', user?.id, user?.department_id],
        queryFn: async () => {
            console.log('📊 Fetching department submissions for stats...')
            const response = await api.get('/departments/submissions')
            console.log('Department Submissions API Response:', response)
            return response.data?.data || response.data || []
        },
        enabled: isDepartmentUser,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    })

    // For form_manager, always fetch submissions and calculate stats (ignore form_id)
    // For admin without form_id, also fetch submissions
    const {
        data: submissionsData,
        isLoading: submissionsLoading,
        refetch: refetchSubmissions,
    } = useQuery({
        queryKey: ['submissions-stats', user?.id],
        queryFn: async () => {
            console.log('📊 Fetching submissions for stats...')
            const response = await api.get('/submissions?per_page=1000')
            return response.data
        },
        enabled: (isFormManager || isAdmin || isDashboardUser) && !isDepartmentUser,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    })

    // Fetch archived count separately (main query excludes archived by default)
    const {
        data: archivedData,
        isLoading: archivedLoading,
        refetch: refetchArchived,
    } = useQuery({
        queryKey: ['archived-count', user?.id],
        queryFn: async () => {
            console.log('📊 Fetching archived count...')
            // api.get already unwraps to response.data via interceptor
            // so 'result' is { success, data: [...], pagination: { total } }
            const result = await api.get('/submissions?archived=true&per_page=1')
            console.log('📊 Archived result:', result)
            return result
        },
        enabled: (isFormManager || isAdmin) && !isDepartmentUser,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    })

    // For dashboard users with form_id, use the existing stats API
    const {
        data: statsData,
        isLoading: statsLoading,
        refetch: refetchStats,
    } = useQuery({
        queryKey: ['stats', formId],
        queryFn: () => formsApi.getStats(formId),
        enabled: !!formId && !isDepartmentUser,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    })

    // Calculate stats from submissions if we fetched them
    let stats = {
        total: 0,
        new_count: 0,
        read_count: 0,
        replied_count: 0,
        pending_department_count: 0,
        department_replied_count: 0,
        closed_count: 0,
        archived_count: 0,
    }

    // Department users use their navigated submissions
    if (isDepartmentUser && Array.isArray(deptSubmissionsData)) {
        const submissions = deptSubmissionsData
        console.log('✅ Using Department User stats calculation')
        console.log('Submissions count:', submissions.length)
        stats = {
            total: submissions.filter(s => !s.archived).length,
            new_count: submissions.filter(s => s.status === 'new' && !s.archived).length,
            read_count: submissions.filter(s => s.status === 'read' && !s.archived).length,
            replied_count: submissions.filter(s => s.status === 'replied' && !s.archived).length,
            pending_department_count: submissions.filter(s => s.status === 'pending_department' && !s.archived).length,
            department_replied_count: submissions.filter(s => s.status === 'department_replied' && !s.archived).length,
            closed_count: submissions.filter(s => s.status === 'closed' && !s.archived).length,
            archived_count: submissions.filter(s => s.archived).length,
        }
    } else if ((isFormManager || isAdmin || isDashboardUser) && Array.isArray(submissionsData)) {
        const submissions = submissionsData
        // Get archived total from separate query
        let archivedTotal = 0
        if (archivedData?.pagination?.total !== undefined) {
            archivedTotal = archivedData.pagination.total
        } else if (Array.isArray(archivedData?.data)) {
            archivedTotal = archivedData.data.length
        }
        console.log('✅ Using all-submissions stats calculation, archived total:', archivedTotal)
        stats = {
            total: submissions.filter(s => !s.archived).length,
            new_count: submissions.filter(s => s.status === 'new' && !s.archived).length,
            read_count: submissions.filter(s => s.status === 'read' && !s.archived).length,
            replied_count: submissions.filter(s => s.status === 'replied' && !s.archived).length,
            pending_department_count: submissions.filter(s => s.status === 'pending_department' && !s.archived).length,
            department_replied_count: submissions.filter(s => s.status === 'department_replied' && !s.archived).length,
            closed_count: submissions.filter(s => s.status === 'closed' && !s.archived).length,
            archived_count: archivedTotal,
        }
    } else if (formId && statsData?.data) {
        console.log('Using stats API data')
        stats = statsData.data
    } else if (Array.isArray(submissionsData)) {
        const submissions = submissionsData
        console.log('Using fallback stats calculation')
        stats = {
            total: submissions.filter(s => !s.archived).length,
            new_count: submissions.filter(s => s.status === 'new' && !s.archived).length,
            read_count: submissions.filter(s => s.status === 'read' && !s.archived).length,
            replied_count: submissions.filter(s => s.status === 'replied' && !s.archived).length,
            pending_department_count: submissions.filter(s => s.status === 'pending_department' && !s.archived).length,
            department_replied_count: submissions.filter(s => s.status === 'department_replied' && !s.archived).length,
            closed_count: submissions.filter(s => s.status === 'closed' && !s.archived).length,
            archived_count: submissions.filter(s => s.archived).length,
        }
    }

    // Determine which query to use for loading/refetch
    const isLoading = isDepartmentUser
        ? deptSubmissionsLoading
        : (isFormManager || isAdmin || isDashboardUser)
            ? submissionsLoading || archivedLoading
            : statsLoading

    const refetch = isDepartmentUser
        ? refetchDeptSubmissions
        : (isFormManager || isAdmin || isDashboardUser)
            ? () => { refetchSubmissions(); refetchArchived(); }
            : refetchStats

    return {
        stats,
        isLoading,
        isError: false,
        error: null,
        refetch,
    }
}

export default useFormStats
